import { set as idbSet } from 'idb-keyval';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

/* Web Share `AbortError` is what the browser throws when the user dismisses
 * the share sheet without choosing a destination. Treating it as a cancel
 * (not an error) lets the UI flash a neutral message instead of "failed". */
export class ExportCancelledError extends Error {
  constructor() {
    super('User cancelled share sheet');
    this.name = 'ExportCancelledError';
  }
}

/**
 * Export a canvas as JPEG. Best-effort across iOS Safari (web PWA), iOS
 * standalone-PWA (Add to Home Screen), and Capacitor (native iOS).
 *
 * Tries in order:
 *   1. Web Share API with files — works in all three modes; produces the
 *      native iOS share sheet (Save to Photos / Send to Mail / etc.).
 *   2. Anchor download — works in the regular browser only; iOS standalone
 *      PWA and Capacitor both ignore <a download>. We still attempt it as a
 *      graceful degradation for non-iOS users (Android, Chrome, Firefox).
 *
 * Throws `ExportCancelledError` when the user dismisses the share sheet,
 * so the caller can distinguish a cancel from a real failure.
 */
export async function exportCanvasJpeg(
  canvas: HTMLCanvasElement,
  filename = `intraop-${Date.now()}.jpg`,
  quality = 0.92,
): Promise<void> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      quality,
    );
  });

  // Android Capacitor needs a dedicated path: the WebView blocks
  // `<a download>` and `navigator.share` for files is unreliable.
  // We write the blob to a cache file via Filesystem, then ask
  // Share to trigger Android's native ShareIntent.
  //
  // iOS is intentionally NOT routed here — the existing
  // navigator.share path already works perfectly inside WKWebView
  // and changing it carries needless risk. Keep iOS on the proven
  // path below.
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    try {
      const base64 = await blobToBase64(blob);
      const writeResult = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      });
      await Share.share({
        title: 'CADtomie Intraop',
        text: filename,
        url: writeResult.uri,
        dialogTitle: 'Share measurement',
      });
      return;
    } catch (err: any) {
      const msg = String(err?.message ?? err ?? '').toLowerCase();
      if (msg.includes('cancel') || msg.includes('canceled') || msg.includes('cancelled')) {
        throw new ExportCancelledError();
      }
      throw err;
    }
  }

  // Web Share API — primary path on iOS (Capacitor WKWebView) and
  // on modern desktop browsers (Chrome / Edge / Safari).
  const file = new File([blob], filename, { type: 'image/jpeg' });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'CADtomie Intraop' });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new ExportCancelledError();
      }
      // iOS Capacitor: rethrow — anchor download won't work in WKWebView either.
      if (typeof window !== 'undefined' && window.location.protocol === 'capacitor:') {
        throw err;
      }
      // Web fall-through to anchor download.
    }
  }

  // Fallback: anchor download / new-tab (desktop / Android Chrome).
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* Convert a Blob to a base64 data string (no `data:` prefix) — what
 * Filesystem.writeFile expects. */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // result is "data:image/jpeg;base64,XXXXX" — strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/** Save the blob to IndexedDB (local gallery). */
export async function saveCanvasLocal(
  canvas: HTMLCanvasElement,
  key = `intraop-${Date.now()}`,
  quality = 0.9,
): Promise<string> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      quality,
    );
  });
  await idbSet(key, blob);
  return key;
}
